import type { JSX, KeyboardEvent } from 'react';
import { Fragment, useCallback, useEffect, useId, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Slider from '@radix-ui/react-slider';
import * as Switch from '@radix-ui/react-switch';
import { useTranslation } from 'react-i18next';
import type { Preferences, PreferenceChange } from '../../preferences/usePreferences.js';
import { AvatarFrame, type SeatPlayer } from './AvatarFrame.js';
import { ClueSlip } from './ClueSlip.js';
import { SecretWordLabel } from './SecretWordLabel.js';
import { VoterCluster } from './VoterCluster.js';
import './MetaControls.css';

/* Committed paths: the App Directory epic must host the pages at exactly
   these URLs (docs/handoffs/app-directory-submission.md). They 404 until
   that epic ships; accepted in the 2026-06-10 brief. */
const TERMS_URL = 'https://susdle.com/terms';
const PRIVACY_URL = 'https://susdle.com/privacy';

interface MetaControlsProps {
  preferences: Preferences;
  onPreferenceChange: PreferenceChange;
  /**
   * Opens an external URL. prod-discord must route through the Activity
   * SDK (sandboxed iframe blocks plain navigation); dev modes pass a
   * window.open fallback.
   */
  openExternalLink: (url: string) => void;
}

/**
 * Fixed-corner meta chrome: two icon-only chapbook-glyph triggers (settings
 * gear, how-to-play query mark) pinned to the viewport's top-right on every
 * phase and viewport. Mounted ONCE at the App shell. Replaces the per-phase
 * Footer that anchored to the spread edge and so shifted (or vanished)
 * between phases. A 1px paper halo traced around each glyph carries
 * legibility over whatever surface sits beneath, including the ghost-POV
 * night desk and the reveal arc's dark stages (see MetaControls.css).
 */
export function MetaControls({
  preferences,
  onPreferenceChange,
  openExternalLink,
}: MetaControlsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="susdle-meta-controls">
      <Dialog.Root>
        <Dialog.Trigger asChild>
          <button
            className="susdle-meta-controls__trigger"
            type="button"
            aria-label={t('metaControls.settings.trigger')}
          >
            <span aria-hidden className="susdle-meta-controls__glyph">⚙</span>
          </button>
        </Dialog.Trigger>
        <ChapbookOverlay title={t('metaControls.settings.title')}>
          <ul className="susdle-prefs">
            <PrefSliderRow
              label={t('settings.music.label')}
              value={preferences.musicVolume}
              onChange={(value) => onPreferenceChange('musicVolume', value)}
            />
            <PrefSliderRow
              label={t('settings.sounds.label')}
              value={preferences.soundsVolume}
              onChange={(value) => onPreferenceChange('soundsVolume', value)}
            />
            <PrefSwitchRow
              label={t('settings.reducedMotion.label')}
              checked={preferences.reducedMotion}
              onChange={(value) => onPreferenceChange('reducedMotion', value)}
            />
          </ul>
          <div className="susdle-colophon">
            <button
              type="button"
              className="susdle-colophon__link"
              onClick={() => openExternalLink(TERMS_URL)}
            >
              {t('metaControls.colophon.terms')}
            </button>
            <span aria-hidden className="susdle-colophon__sep">·</span>
            <button
              type="button"
              className="susdle-colophon__link"
              onClick={() => openExternalLink(PRIVACY_URL)}
            >
              {t('metaControls.colophon.privacy')}
            </button>
          </div>
        </ChapbookOverlay>
      </Dialog.Root>
      <Dialog.Root>
        <Dialog.Trigger asChild>
          <button
            className="susdle-meta-controls__trigger"
            type="button"
            aria-label={t('metaControls.howToPlay.trigger')}
          >
            <span aria-hidden className="susdle-meta-controls__glyph susdle-meta-controls__glyph--query">
              ?
            </span>
          </button>
        </Dialog.Trigger>
        <ChapbookOverlay title={t('metaControls.howToPlay.title')} wide>
          <HowToPlayBody />
        </ChapbookOverlay>
      </Dialog.Root>
    </div>
  );
}

/** Worked-example cast for the Overview: a 4-player game (2 Villagers, 1 Ghost,
 *  1 Fool — the minimum legal game that contains a Fool). Seat tones mirror the
 *  shipped default-avatar palette. Names + clues are static teaching copy under
 *  `howToPlay.example.*`. */
const EXAMPLE_CAST = [
  { id: 'michael', tone: 1 },
  { id: 'peepsie', tone: 2 },
  { id: 'meepsie', tone: 5 },
  { id: 'briar', tone: 4 },
] as const;

const GHOST_ID = 'meepsie';
const RULE_ROLES = ['villager', 'ghost', 'fool'] as const;
const RULE_PHASES = ['assignment', 'clue', 'discussion', 'reveal'] as const;
const TAB_ORDER = ['overview', 'rules'] as const;
type HowToTab = (typeof TAB_ORDER)[number];

/**
 * The How-to-play surface: two tabs inside the chapbook overlay. **Overview**
 * (the default landing) teaches a brand-new player with a fully worked round
 * built from the real in-game frames; **Rules** is the look-up reference (roles,
 * phases, voting). All public information, safe to open mid-game without
 * spoiling a hidden role. Copy lives under `howToPlay.*`; the worked example is
 * static English teaching content (`howToPlay.example.*`) — the per-locale
 * example-word seam is deferred. Tabs are a hand-rolled ARIA tablist (no Radix
 * Tabs dependency); both panels mount so `aria-controls` stays valid, the
 * inactive one hidden.
 */
function HowToPlayBody(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<HowToTab>('overview');
  const baseId = useId();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onTabKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>): void => {
    const idx = TAB_ORDER.indexOf(tab);
    let next: HowToTab | null = null;
    if (e.key === 'ArrowRight') next = TAB_ORDER[(idx + 1) % TAB_ORDER.length] ?? null;
    else if (e.key === 'ArrowLeft') next = TAB_ORDER[(idx - 1 + TAB_ORDER.length) % TAB_ORDER.length] ?? null;
    else if (e.key === 'Home') next = TAB_ORDER[0];
    else if (e.key === 'End') next = TAB_ORDER[TAB_ORDER.length - 1] ?? null;
    if (next) {
      e.preventDefault();
      setTab(next);
      tabRefs.current[next]?.focus();
    }
  }, [tab]);

  return (
    <div className="susdle-howto">
      <div className="susdle-howto__tabs" role="tablist" aria-label={t('metaControls.howToPlay.title')}>
        {TAB_ORDER.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`${baseId}-${key}-tab`}
            aria-selected={tab === key}
            aria-controls={`${baseId}-${key}-panel`}
            tabIndex={tab === key ? 0 : -1}
            ref={(el) => { tabRefs.current[key] = el; }}
            className="susdle-howto__tab"
            onClick={() => setTab(key)}
            onKeyDown={onTabKeyDown}
          >
            {t(`howToPlay.tabs.${key}`)}
          </button>
        ))}
      </div>
      {TAB_ORDER.map((key) => (
        <div
          key={key}
          role="tabpanel"
          id={`${baseId}-${key}-panel`}
          aria-labelledby={`${baseId}-${key}-tab`}
          className="susdle-howto__panel"
          hidden={tab !== key}
        >
          {key === 'overview' ? <OverviewTab /> : <RulesTab />}
        </div>
      ))}
    </div>
  );
}

/** Overview tab: goal line spanning the spread, then a two-column worked round.
 *  Left = role plates (what each role sees) + the Clue-Giving cast; right = the
 *  Discussion vote piled on the Ghost + the Role Reveal. Section rhythm:
 *  centered head → left-aligned prose → centered frame(s). */
function OverviewTab(): JSX.Element {
  const { t } = useTranslation();
  const secretWord = t('howToPlay.example.secretWord');
  const cast = EXAMPLE_CAST.map((c) => ({
    seat: {
      id: `howto-${c.id}`,
      name: t(`howToPlay.example.cast.${c.id}`),
      avatarUrl: '',
      isDefaultAvatar: true,
      defaultAvatarIndex: c.tone,
    } satisfies SeatPlayer,
    clue: t(`howToPlay.example.clue.${c.id}`),
    isGhost: c.id === GHOST_ID,
  }));
  const ghost = cast.find((c) => c.isGhost) ?? cast[0]!;
  const voters = cast.filter((c) => !c.isGhost).map((c) => c.seat);

  return (
    <>
      <p className="susdle-howto__goal">{t('howToPlay.goal')}</p>
      <div className="susdle-howto__spread">
        <div className="susdle-howto__col">
          <h3 className="susdle-howto__head">{t('howToPlay.overview.seesHeading')}</h3>
          <div className="susdle-howto__lenses">
            <div className="susdle-howto__lens">
              <span className="susdle-howto__lens-role">{t('howToPlay.overview.lens.villager')}</span>
              <SecretWordLabel mode="word" word={secretWord} />
            </div>
            <div className="susdle-howto__lens">
              <span className="susdle-howto__lens-role">{t('howToPlay.overview.lens.ghost')}</span>
              <SecretWordLabel mode="length" length={secretWord.length} />
            </div>
            <div className="susdle-howto__lens">
              <span className="susdle-howto__lens-role">{t('howToPlay.overview.lens.fool')}</span>
              <SecretWordLabel mode="word" word={t('howToPlay.example.foolWord')} />
            </div>
          </div>
          <h3 className="susdle-howto__head">{t('howToPlay.overview.clueHeading')}</h3>
          <p className="susdle-howto__lead">{t('howToPlay.overview.clueLead')}</p>
          <div className="susdle-howto__cast">
            {cast.map((c) => (
              <ExampleTile key={c.seat.id} player={c.seat} clue={c.clue} />
            ))}
          </div>
        </div>
        <div className="susdle-howto__col">
          <h3 className="susdle-howto__head">{t('howToPlay.overview.discussionHeading')}</h3>
          <p className="susdle-howto__lead">{t('howToPlay.overview.discussionLead')}</p>
          <div className="susdle-howto__cast">
            {cast.map((c) => (
              <ExampleTile
                key={c.seat.id}
                player={c.seat}
                clue={c.clue}
                votedOut={c.isGhost}
                voters={c.isGhost ? voters : []}
              />
            ))}
          </div>
          <h3 className="susdle-howto__head">{t('howToPlay.overview.revealHeading')}</h3>
          <p className="susdle-howto__lead">{t('howToPlay.overview.revealLead')}</p>
          <div className="susdle-howto__reveal">
            <ExampleTile player={ghost.seat} votedOut revealed />
          </div>
        </div>
      </div>
    </>
  );
}

/** Rules tab: Roles reference (left) + Game Phases and Voting (right). Roles
 *  use a name-led muted bulleted list; phases use a definition list. */
function RulesTab(): JSX.Element {
  const { t } = useTranslation();
  const voting = t('howToPlay.rules.voting', { returnObjects: true }) as string[];
  return (
    <div className="susdle-howto__spread">
      <div className="susdle-howto__col">
        <h3 className="susdle-howto__head">{t('howToPlay.rules.rolesHeading')}</h3>
        <div className="susdle-howto__roles">
          {RULE_ROLES.map((role) => {
            const facts = t(`howToPlay.rules.roles.${role}.facts`, { returnObjects: true }) as string[];
            return (
              <div className="susdle-howto__role" key={role}>
                <h4 className="susdle-howto__role-name">{t(`howToPlay.rules.roles.${role}.name`)}</h4>
                <ul className="susdle-howto__role-facts">
                  {facts.map((fact) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
      <div className="susdle-howto__col">
        <h3 className="susdle-howto__head">{t('howToPlay.rules.phasesHeading')}</h3>
        <dl className="susdle-howto__phases">
          {RULE_PHASES.map((phase) => (
            <Fragment key={phase}>
              <dt className="susdle-howto__phase-name">{t(`howToPlay.rules.phases.${phase}.name`)}</dt>
              <dd className="susdle-howto__phase-body">{t(`howToPlay.rules.phases.${phase}.body`)}</dd>
            </Fragment>
          ))}
        </dl>
        <h3 className="susdle-howto__head">{t('howToPlay.rules.votingHeading')}</h3>
        {voting.map((line) => (
          <p className="susdle-howto__line" key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

/** One worked-example tile: the real AvatarFrame (static — no motion/float),
 *  optionally with a ClueSlip beneath, the Discussion VoterCluster piled on
 *  top, the Oxblood voted-out plate (`data-eliminated`), and the spectral Ghost
 *  halo on reveal. */
function ExampleTile({
  player,
  clue,
  votedOut = false,
  revealed = false,
  voters,
}: {
  player: SeatPlayer;
  clue?: string | null;
  votedOut?: boolean;
  revealed?: boolean;
  voters?: readonly SeatPlayer[];
}): JSX.Element {
  return (
    <div className="susdle-howto__tile">
      <div
        className={`susdle-howto__tile-frame${revealed ? ' susdle-howto__tile-frame--revealed' : ''}`}
        data-eliminated={votedOut ? 'yes' : undefined}
      >
        {voters && voters.length > 0 ? <VoterCluster voters={voters} justArrived /> : null}
        <AvatarFrame
          player={player}
          isHost={false}
          motionEnabled={false}
          suppressEntry
          suppressFloat
        />
      </div>
      {clue !== undefined ? <ClueSlip clue={clue} ownerName={player.name} suppressInk /> : null}
    </div>
  );
}

/** Volume slider row. Label is rendered as a <span> with a generated id,
 *  bound to the Slider via aria-labelledby (label-click toggling makes no
 *  semantic sense for a slider, so this row drops the <label> wrapper that
 *  the switch row uses). 5% step is the Radix Slider arrow-key default and
 *  matches the brief; track-tap jumps to position; thumb drag tracks pointer. */
function PrefSliderRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}): JSX.Element {
  const labelId = useId();
  /* CSS :active on the slider root is unreliable during a Radix Slider drag
     (Radix uses setPointerCapture on the thumb, which redirects pointer
     events and prevents :active from propagating to the root). We track the
     drag with window-scoped pointerup/pointercancel listeners attached for
     the lifetime of the drag — robust to release-outside-window and to
     capture, and torn down by the effect cleanup so a mid-drag unmount
     (overlay closes) can't leave window listeners behind. */
  const [isDragging, setIsDragging] = useState(false);
  const handlePointerDown = useCallback(() => setIsDragging(true), []);
  useEffect(() => {
    if (!isDragging) return undefined;
    const end = (): void => setIsDragging(false);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [isDragging]);
  return (
    <li className="susdle-prefs__row">
      <div className="susdle-prefs__label">
        <span id={labelId} className="susdle-prefs__label-text">
          {label}
        </span>
        <Slider.Root
          className={`susdle-slider__root${isDragging ? ' is-dragging' : ''}`}
          value={[value]}
          onValueChange={([next]) => {
            if (typeof next === 'number') onChange(next);
          }}
          onPointerDown={handlePointerDown}
          min={0}
          max={1}
          step={0.05}
          aria-labelledby={labelId}
        >
          <Slider.Track className="susdle-slider__track">
            <Slider.Range className="susdle-slider__range" />
          </Slider.Track>
          <Slider.Thumb className="susdle-slider__thumb" aria-label={label}>
            {/* aria-hidden: Radix Thumb already exposes aria-valuenow + the
                slider's aria-labelledby, so the bubble would double-read. */}
            <span className="susdle-slider__value" aria-hidden>
              {Math.round(value * 100)}%
            </span>
          </Slider.Thumb>
        </Slider.Root>
      </div>
    </li>
  );
}

function PrefSwitchRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}): JSX.Element {
  return (
    <li className="susdle-prefs__row">
      <label className="susdle-prefs__label">
        <span className="susdle-prefs__label-text">{label}</span>
        <Switch.Root
          className="susdle-toggle__root"
          checked={checked}
          onCheckedChange={onChange}
        >
          <Switch.Thumb className="susdle-toggle__thumb" />
        </Switch.Root>
      </label>
    </li>
  );
}

function ChapbookOverlay({
  title,
  children,
  wide = false,
}: {
  title: string;
  children: React.ReactNode;
  /** Desktop (≥880px) widens the card for two-column content (how-to-play
   *  spread); narrow viewports ignore it. */
  wide?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog.Portal>
      <Dialog.Overlay className="susdle-chapbook-overlay__bg" />
      <Dialog.Content
        className={`susdle-chapbook-overlay${wide ? ' susdle-chapbook-overlay--wide' : ''}`}
      >
        <Dialog.Title className="susdle-chapbook-overlay__title susdle-display">
          {title}
        </Dialog.Title>
        <Dialog.Description asChild>
          <div className="susdle-chapbook-overlay__body">
            {children}
          </div>
        </Dialog.Description>
        <Dialog.Close asChild>
          <button
            className="susdle-chapbook-overlay__close"
            type="button"
            aria-label={t('metaControls.overlay.close')}
          >
            <span aria-hidden>×</span>
          </button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Portal>
  );
}
